import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userOrders, API_URL } from '../utils/api';
import { Button } from '../components/ui/button';
import { Package, Truck, CheckCircle2, Clock, ShoppingBag, ChevronRight, ArrowLeft, ExternalLink, MapPin, MessageSquare, ArrowRight, AlertCircle, RefreshCw } from 'lucide-react';
import { SEO } from '../components/SEO';

const STATUS_CONFIG = {
  new_order: { label: 'Order Placed', color: 'bg-blue-500', text: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', icon: ShoppingBag },
  payment_confirmed: { label: 'Payment Confirmed', color: 'bg-emerald-500', text: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: CheckCircle2 },
  processing: { label: 'Processing', color: 'bg-amber-500', text: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', icon: Clock },
  picking: { label: 'Picking', color: 'bg-amber-500', text: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', icon: Package },
  packed: { label: 'Packed', color: 'bg-amber-500', text: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', icon: Package },
  ready_to_ship: { label: 'Ready to Ship', color: 'bg-purple-500', text: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', icon: Package },
  shipped: { label: 'Shipped', color: 'bg-indigo-500', text: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200', icon: Truck },
  out_for_delivery: { label: 'Out for Delivery', color: 'bg-indigo-500', text: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-emerald-500', text: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-red-500', text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', icon: AlertCircle },
  returned: { label: 'Returned', color: 'bg-orange-500', text: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', icon: RefreshCw },
  refunded: { label: 'Refunded', color: 'bg-gray-500', text: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', icon: RefreshCw },
  on_hold: { label: 'On Hold', color: 'bg-yellow-500', text: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200', icon: Clock },
};

const TIMELINE_STEPS = [
  { key: 'order_placed', label: 'Order Placed', icon: ShoppingBag },
  { key: 'processing', label: 'Processing', icon: Clock },
  { key: 'shipped', label: 'Shipped', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle2 },
];

const getTimelineStep = (status) => {
  if (['new_order', 'payment_confirmed'].includes(status)) return 0;
  if (['processing', 'picking', 'packed', 'ready_to_ship'].includes(status)) return 1;
  if (['shipped', 'out_for_delivery'].includes(status)) return 2;
  if (['delivered'].includes(status)) return 3;
  return -1;
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '-';
const fmtTime = (d) => d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-';

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.processing;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${cfg.bg} ${cfg.text} ${cfg.border} border`} data-testid={`status-badge-${status}`}>
      <Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
};

// ═══ ORDER LIST VIEW ═══
const OrderList = ({ orders, onSelect }) => (
  <div className="space-y-3" data-testid="order-list">
    {orders.map(order => {
      const firstItem = order.items?.[0];
      const itemCount = order.items?.reduce((s, i) => s + (i.quantity || 1), 0) || 0;
      const img = firstItem?.product?.images?.[0];
      return (
        <button
          key={order.id}
          onClick={() => onSelect(order.id)}
          className="w-full bg-white rounded-xl border border-[#E2E2E2] p-4 md:p-5 flex items-center gap-4 hover:border-[#C9A84C]/30 hover:shadow-sm transition-all text-left group"
          data-testid={`order-card-${order.id}`}
        >
          {/* Product thumbnail */}
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg bg-[#EFE5D6]/30 overflow-hidden flex-shrink-0">
            <img
              src={img ? `${API_URL}${img}` : 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=120'}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>

          {/* Order info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-bold text-[#0A0A0A]" data-testid={`order-number-${order.id}`}>#{order.id?.slice(0, 8).toUpperCase()}</span>
              <StatusBadge status={order.fulfilment_status || 'new_order'} />
            </div>
            <p className="text-xs text-[#666] mt-1">{fmtDate(order.created_at)}</p>
            <p className="text-xs text-[#666] mt-0.5">{itemCount} {itemCount === 1 ? 'item' : 'items'}</p>
          </div>

          {/* Total + Arrow */}
          <div className="text-right flex-shrink-0">
            <p className="text-lg font-bold text-[#0A0A0A]" data-testid={`order-total-${order.id}`}>&pound;{order.total?.toFixed(2)}</p>
            <ChevronRight className="w-4 h-4 text-[#E2E2E2] group-hover:text-[#C9A84C] transition-colors ml-auto mt-1" />
          </div>
        </button>
      );
    })}
  </div>
);

// ═══ ORDER DETAIL VIEW ═══
const OrderDetail = ({ order, onBack }) => {
  const currentStep = getTimelineStep(order.fulfilment_status || 'new_order');
  const isCancelled = ['cancelled', 'returned', 'refunded', 'on_hold'].includes(order.fulfilment_status);

  return (
    <div className="space-y-6" data-testid="order-detail">
      {/* Back button */}
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-[#666] hover:text-[#C9A84C] transition-colors" data-testid="back-to-orders">
        <ArrowLeft className="w-4 h-4" /> Back to Orders
      </button>

      {/* Order Header */}
      <div className="bg-white rounded-2xl border border-[#E2E2E2] p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#666]">Order</p>
            <h2 className="text-2xl font-bold text-[#0A0A0A] mt-0.5" data-testid="detail-order-number">#{order.id?.slice(0, 8).toUpperCase()}</h2>
            <p className="text-sm text-[#666] mt-1">Placed on {fmtDate(order.created_at)}</p>
          </div>
          <div className="md:text-right">
            <StatusBadge status={order.fulfilment_status || 'new_order'} />
            <p className="text-2xl font-bold text-[#0A0A0A] mt-2" data-testid="detail-order-total">&pound;{order.total?.toFixed(2)}</p>
            {order.discount && (
              <p className="text-xs text-emerald-600 mt-0.5">Discount ({order.discount.code}): -&pound;{order.discount.amount?.toFixed(2)}</p>
            )}
          </div>
        </div>
      </div>

      {/* Status Timeline */}
      {!isCancelled && (
        <div className="bg-white rounded-2xl border border-[#E2E2E2] p-6 md:p-8" data-testid="order-timeline">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-6">Order Progress</h3>
          <div className="flex items-center justify-between relative">
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-[#E2E2E2]" />
            <div className="absolute top-5 left-0 h-0.5 bg-[#C9A84C] transition-all duration-700" style={{ width: `${Math.max(0, (currentStep / (TIMELINE_STEPS.length - 1)) * 100)}%` }} />
            {TIMELINE_STEPS.map((step, i) => {
              const Icon = step.icon;
              const isComplete = i <= currentStep;
              const isCurrent = i === currentStep;
              return (
                <div key={step.key} className="relative flex flex-col items-center z-10" data-testid={`timeline-${step.key}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                    isComplete ? 'bg-[#C9A84C] border-[#C9A84C] text-white' : 'bg-white border-[#E2E2E2] text-[#666]/30'
                  } ${isCurrent ? 'ring-4 ring-[#C9A84C]/20 scale-110' : ''}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <p className={`text-[10px] font-bold uppercase tracking-wider mt-2 text-center max-w-[80px] ${
                    isComplete ? 'text-[#0A0A0A]' : 'text-[#666]/40'
                  }`}>{step.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tracking Info */}
      {order.tracking && order.tracking.courier && (
        <div className="bg-white rounded-2xl border border-[#E2E2E2] p-6 md:p-8" data-testid="order-tracking">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-4">Tracking Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] uppercase text-[#666]">Courier</p>
              <p className="text-sm font-bold text-[#0A0A0A]" data-testid="tracking-courier">{order.tracking.courier}</p>
            </div>
            {order.tracking.tracking_number && (
              <div>
                <p className="text-[10px] uppercase text-[#666]">Tracking Number</p>
                <p className="text-sm font-bold text-[#0A0A0A] font-mono" data-testid="tracking-number">{order.tracking.tracking_number}</p>
              </div>
            )}
            {order.tracking.dispatch_date && (
              <div>
                <p className="text-[10px] uppercase text-[#666]">Dispatched</p>
                <p className="text-sm font-bold text-[#0A0A0A]">{fmtDate(order.tracking.dispatch_date)}</p>
              </div>
            )}
            {order.tracking.expected_delivery && (
              <div>
                <p className="text-[10px] uppercase text-[#666]">Expected Delivery</p>
                <p className="text-sm font-bold text-[#0A0A0A]">{fmtDate(order.tracking.expected_delivery)}</p>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {order.tracking.tracking_url && (
              <a href={order.tracking.tracking_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-5 py-2.5 rounded-lg text-[10px] font-bold uppercase tracking-wider hover:bg-[#0A0A0A] transition-colors"
                data-testid="track-parcel-btn">
                <ExternalLink className="w-3.5 h-3.5" /> Track Parcel
              </a>
            )}
            <Link to="/track-order"
              className="inline-flex items-center gap-2 border border-[#E2E2E2] text-[#666] px-5 py-2.5 rounded-lg text-[10px] font-bold uppercase tracking-wider hover:border-[#C9A84C] hover:text-[#C9A84C] transition-colors"
              data-testid="track-order-link">
              <MapPin className="w-3.5 h-3.5" /> Track Order Page
            </Link>
          </div>
        </div>
      )}

      {/* Products */}
      <div className="bg-white rounded-2xl border border-[#E2E2E2] p-6 md:p-8" data-testid="order-items">
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-4">Items ({order.items?.length || 0})</h3>
        <div className="space-y-3">
          {(order.items || []).map((item, i) => {
            const p = item.product || {};
            const img = p.images?.[0];
            return (
              <div key={i} className="flex items-center gap-4 py-3 border-b border-[#E2E2E2]/50 last:border-0" data-testid={`order-item-${i}`}>
                <div className="w-16 h-20 rounded-lg bg-[#EFE5D6]/20 overflow-hidden flex-shrink-0">
                  <img
                    src={img ? `${API_URL}${img}` : 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=120'}
                    alt={p.name || 'Product'}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#0A0A0A]">{p.name || 'Product'}</p>
                  <p className="text-[10px] text-[#666] mt-0.5">
                    {p.category && <span className="uppercase">{p.category} &middot; </span>}
                    Size: {item.size || '-'} &middot; Qty: {item.quantity || 1}
                  </p>
                </div>
                <p className="text-sm font-bold text-[#0A0A0A] flex-shrink-0">&pound;{((p.price || 0) * (item.quantity || 1)).toFixed(2)}</p>
              </div>
            );
          })}
        </div>

        {/* Order Summary */}
        <div className="mt-4 pt-4 border-t border-[#E2E2E2] space-y-1.5">
          {order.subtotal && order.subtotal !== order.total && (
            <div className="flex justify-between text-sm text-[#666]">
              <span>Subtotal</span>
              <span>&pound;{order.subtotal.toFixed(2)}</span>
            </div>
          )}
          {order.discount && (
            <div className="flex justify-between text-sm text-emerald-600">
              <span>Discount ({order.discount.code})</span>
              <span>-&pound;{order.discount.amount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm text-[#666]">
            <span>Shipping</span>
            <span className="text-emerald-600">Free</span>
          </div>
          <div className="flex justify-between text-base font-bold text-[#0A0A0A] pt-1.5 border-t border-[#E2E2E2]">
            <span>Total</span>
            <span>&pound;{order.total?.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Shipping Address */}
      {order.shipping_address && (
        <div className="bg-white rounded-2xl border border-[#E2E2E2] p-6 md:p-8" data-testid="order-address">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-3">Shipping Address</h3>
          <div className="text-sm text-[#0A0A0A] space-y-0.5">
            <p className="font-medium">{order.shipping_address.first_name} {order.shipping_address.last_name}</p>
            <p>{order.shipping_address.address_line_1}</p>
            {order.shipping_address.address_line_2 && <p>{order.shipping_address.address_line_2}</p>}
            <p>{order.shipping_address.city}{order.shipping_address.county ? `, ${order.shipping_address.county}` : ''}</p>
            <p>{order.shipping_address.postcode}</p>
            <p>{order.shipping_address.country}</p>
          </div>
        </div>
      )}

      {/* Status History */}
      {order.status_history && order.status_history.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E2E2E2] p-6 md:p-8" data-testid="order-status-history">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-4">Status Updates</h3>
          <div className="space-y-3">
            {[...order.status_history].reverse().map((h, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${i === 0 ? 'bg-[#C9A84C]' : 'bg-[#E2E2E2]'}`} />
                <div>
                  <p className={`text-sm font-medium ${i === 0 ? 'text-[#0A0A0A]' : 'text-[#666]'}`}>
                    {(h.to_status || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </p>
                  {h.note && <p className="text-xs text-[#666]/60 mt-0.5">{h.note}</p>}
                  <p className="text-[10px] text-[#666]/40">{fmtTime(h.timestamp)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="bg-[#0A0A0A] text-white rounded-2xl p-6 md:p-8" data-testid="order-actions">
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-4">Need Help?</h3>
        <div className="flex flex-wrap gap-3">
          <Link to="/track-order" data-testid="action-track-order">
            <Button className="bg-[#C9A84C] text-white hover:bg-[#B8733A] text-[10px] uppercase tracking-wider font-bold px-6">
              <MapPin className="w-3.5 h-3.5 mr-2" /> Track Order
            </Button>
          </Link>
          <Link to="/contact" data-testid="action-contact-support">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 text-[10px] uppercase tracking-wider font-bold px-6">
              <MessageSquare className="w-3.5 h-3.5 mr-2" /> Contact Support
            </Button>
          </Link>
          <Link to="/shop" data-testid="action-continue-shopping">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 text-[10px] uppercase tracking-wider font-bold px-6">
              <ShoppingBag className="w-3.5 h-3.5 mr-2" /> Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

// ═══ MAIN PAGE ═══
export default function MyOrders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    loadOrders();
  }, [user]);

  const loadOrders = async () => {
    try {
      const data = await userOrders.getAll();
      setOrders(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSelectOrder = async (orderId) => {
    try {
      const detail = await userOrders.getById(orderId);
      setSelectedOrder(detail);
      window.scrollTo(0, 0);
    } catch (e) { console.error(e); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
      <div className="w-8 h-8 border-2 border-[#E2E2E2] border-t-[#C9A84C] rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="my-orders-page">
      <SEO title="My Orders" description="View your order history, track deliveries, and manage your purchases at Vintage Wardrobe Co." path="/my-orders" />
      {/* Hero */}
      <div className="bg-[#0A0A0A] text-white py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight" data-testid="my-orders-title">My Orders</h1>
          <p className="text-sm text-white/40 mt-2">View your order history, track deliveries, and manage your purchases.</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {selectedOrder ? (
          <OrderDetail order={selectedOrder} onBack={() => setSelectedOrder(null)} />
        ) : orders.length === 0 ? (
          <div className="text-center py-20" data-testid="no-orders">
            <Package className="w-14 h-14 text-[#E2E2E2] mx-auto mb-4" />
            <h2 className="text-xl font-heading font-semibold mb-2">No orders yet</h2>
            <p className="text-sm text-[#666] mb-6">When you place your first order, it will appear here.</p>
            <Link to="/shop">
              <Button className="bg-[#0A0A0A] text-white hover:bg-[#0A0A0A] px-8 text-sm uppercase tracking-wider font-bold">
                <ShoppingBag className="w-4 h-4 mr-2" /> Start Shopping
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-[#666]">{orders.length} {orders.length === 1 ? 'order' : 'orders'}</p>
              <Link to="/track-order" className="text-[10px] font-bold uppercase tracking-wider text-[#C9A84C] hover:text-[#B8733A] flex items-center gap-1 transition-colors" data-testid="go-to-track-order">
                Track an Order <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <OrderList orders={orders} onSelect={handleSelectOrder} />
          </>
        )}
      </div>
    </div>
  );
}
